import { withEmailId } from '../utils/auth';
// Approval status values, product-type names and version helpers shared by the
// Creatives, Approval Dashboard and Approval Detailed View screens.
export const APPROVAL_STATUS = {
  NOT_SUBMITTED: 'Not submitted',
  SUBMITTED: 'Submitted for Approval',
  IN_APPROVAL: 'In Approval',
  SENT_BACK: 'Sent back to User',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REVALIDATED: 'Revalidated',
};

export const approvalCapsuleClass = (status) => {
  switch (status) {
    case APPROVAL_STATUS.APPROVED:
      return 'workflow-capsule-good';
    case APPROVAL_STATUS.REJECTED:
      return 'workflow-capsule-bad';
    case APPROVAL_STATUS.SENT_BACK:
      return 'workflow-capsule-warn';
    case APPROVAL_STATUS.SUBMITTED:
    case APPROVAL_STATUS.IN_APPROVAL:
      return 'workflow-capsule-info';
    default:
      return 'workflow-capsule-neutral';
  }
};

export const lastActionText = (status, { pendingWith, lastActionBy, submittedBy, revalidatedBy } = {}) => {
  switch (status) {
    case APPROVAL_STATUS.SUBMITTED:
    case APPROVAL_STATUS.IN_APPROVAL:
      return `Pending with ${pendingWith || '—'}`;
    case APPROVAL_STATUS.APPROVED:
      return lastActionBy ? `Approved by ${lastActionBy}` : '—';
    case APPROVAL_STATUS.REVALIDATED: {
      const actor = revalidatedBy || submittedBy || lastActionBy;
      return actor ? `Revalidated by ${actor}` : '—';
    }
    case APPROVAL_STATUS.SENT_BACK:
      return lastActionBy ? `Sent back by ${lastActionBy}` : '—';
    case APPROVAL_STATUS.REJECTED:
      return lastActionBy ? `Rejected by ${lastActionBy}` : '—';
    default:
      return '—';
  }
};

export const APPROVER_STATUS = {
  PENDING: 'Pending',
  NOTIFIED: 'Notified',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SENT_BACK: 'Sent back to User',
};

export const APPROVAL_STATUS_COLOR = '#333';

export const productTypeNames = (productType) =>
  String(productType || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

// media_link is a full path / URL, so a row shows just the file it points at.
export const fileNameFromLink = (link) => {
  if (!link) return '';
  const withoutQuery = String(link).split('?')[0];
  const fileName = withoutQuery.split('/').pop() || withoutQuery;
  try {
    return decodeURIComponent(fileName);
  } catch (error) {
    return fileName;
  }
};


export const formatTimestamp = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};


export const versionNumber = (value) => {
  const digits = String(value ?? '').replace(/[^0-9]/g, '');
  return digits ? Number(digits) : null;
};

export const aiSummaryText = (version) => {
  if (version.ai_status === 'Passed') return 'AI: Passed';
  if (version.ai_status === 'Failed') {
    const count = version.non_meeting || 0;
    return `AI: ${count} issue${count === 1 ? '' : 's'}`;
  }
  return 'AI: Pending';
};

export const toReuploadVersions = (rawVersions) =>
  (rawVersions || []).map((version, index, all) => ({
    parentID: version.parent_id,
    versionNo: versionNumber(version.version) ?? all.length - index,
    label: fileNameFromLink(version.name) || `Version ${all.length - index}`,
    aiStatus: version.ai_status,
    aiSummary: aiSummaryText(version),
    createdBy: version.validated_by || '—',
    updatedAt: formatTimestamp(version.timestamp),
    validationStatus: version.validation_status,
    meeting: version.meeting || 0,
    nonMeeting: version.non_meeting || 0,
    notApplicable: version.not_applicable || 0,
  }));

// Loads what the re-upload form is pre-filled with -- product types, media type,
// language, system id, next version and the earlier versions -- straight from the
// database, and shapes it as Validate Content's `revalidate` state. parentID can be
// any version of the creative; the form always opens on the latest one.
// Throws with the API's message when the creative cannot be loaded.
export const fetchReuploadState = async (parentID, { origin, returnTo }) => {
  const response = await fetch(
    withEmailId(`/mcv/creative_reupload_details?parent_id=${encodeURIComponent(parentID)}`)
  );
  const data = await response.json().catch(() => ({}));
  const body = data.detail || data;
  if (!response.ok || body.status !== 'SUCCESS' || !body.data) {
    throw new Error(body.data || 'Could not load this creative for re-upload.');
  }

  const creative = body.data;
  return {
    creativeID: creative.creative_id,
    systemID: creative.system_id ?? null,
    parentID: creative.latest_parent_id,
    name: fileNameFromLink(creative.name),
    mediaType: creative.media_type || '',
    language: creative.language || '',
    productTypes: productTypeNames(creative.product_type),
    nextVersion: creative.next_version,
    versions: toReuploadVersions(creative.versions),
    origin,
    returnTo,
  };
};

// Window event a screen fires after changing a left-panel badge count in place,
// so HomeScreen re-reads the counts at once instead of on its next timer tick.
export const NOTIFICATIONS_CHANGED_EVENT = 'mcv:notifications-changed';
